import { useEffect, useState, useCallback } from 'react'
import { api } from '../../lib/api'
import { useToast } from '../ui/Toast'
import { Button } from '../ui/Button'
import { AgentStatusCard } from '../remote/AgentStatusCard'
import { AgentInstallModal } from '../remote/AgentInstallModal'
import { SessionLauncher } from '../remote/SessionLauncher'
import type { RemoteSession, SiteDeviceWithSpec } from '@shared/types'

interface AgentData {
  configured: boolean
  agent?: {
    id: string
    status: string
    version: string | null
    hostname: string | null
    platform: string | null
    local_subnet: string | null
    last_heartbeat: string | null
    uptime_seconds: number | null
    active_sessions: number
    device_count: number
    online_devices: number
    rdp_available: number
  }
}

interface SessionsData {
  sessions: RemoteSession[]
  total: number
  stats: {
    total_this_month: number
    most_accessed_device: string | null
    most_accessed_count: number
  }
}

interface Props {
  siteId: string
  siteName: string
  onOpenDevice?: (slug: string) => void
}

export function SiteRemoteAccessTab({ siteId, siteName }: Props) {
  const [agentStatus, setAgentStatus] = useState<AgentData | null>(null)
  const [sessions, setSessions] = useState<SessionsData | null>(null)
  const [devices, setDevices] = useState<SiteDeviceWithSpec[]>([])
  const [loading, setLoading] = useState(true)
  const [showInstallModal, setShowInstallModal] = useState(false)
  const [showAllSessions, setShowAllSessions] = useState(false)

  const load = useCallback(() => {
    Promise.all([
      api.get<AgentData>(`/sites/${siteId}/agent/status`).catch(() => ({ configured: false } as AgentData)),
      api.get<SessionsData>(`/sites/${siteId}/sessions?limit=50`),
      api.get<{ data: SiteDeviceWithSpec[] }>(`/sites/${siteId}/devices?limit=200`),
    ]).then(([a, s, d]) => {
      setAgentStatus(a)
      setSessions(s)
      setDevices(d.data || [])
    }).catch(() => {})
      .finally(() => setLoading(false))
  }, [siteId])

  useEffect(() => { load() }, [load])

  const exportCsv = () => {
    window.open(`/api/sites/${siteId}/sessions/export`, '_blank')
  }

  if (loading) return <div style={{ padding: 24, color: 'var(--text-3)' }}>Loading remote access...</div>

  const agentConnected = !!agentStatus?.configured && agentStatus.agent?.status === 'active' &&
    !!agentStatus.agent?.last_heartbeat &&
    (Date.now() - new Date(agentStatus.agent.last_heartbeat).getTime()) < 120000

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Agent Status Card */}
      <AgentStatusCard
        siteId={siteId}
        agent={agentStatus}
        onInstall={() => setShowInstallModal(true)}
        onRefresh={load}
      />

      {/* Quick Access / Session Launcher */}
      <SessionLauncher
        siteId={siteId}
        devices={devices}
        agentConnected={agentConnected}
      />

      {/* Session History */}
      <SessionHistory
        sessions={sessions}
        showAll={showAllSessions}
        onToggleAll={() => setShowAllSessions(!showAllSessions)}
        onExport={exportCsv}
      />

      {/* Install Modal */}
      {showInstallModal && (
        <AgentInstallModal
          siteId={siteId}
          onClose={() => setShowInstallModal(false)}
          onInstalled={() => {
            setShowInstallModal(false)
            load()
          }}
        />
      )}
    </div>
  )
}

function SessionHistory({
  sessions, showAll, onToggleAll, onExport,
}: {
  sessions: SessionsData | null
  showAll: boolean
  onToggleAll: () => void
  onExport: () => void
}) {
  if (!sessions) return null

  const displayed = showAll ? sessions.sessions : sessions.sessions.slice(0, 10)

  return (
    <div style={{
      background: 'var(--bg-surface)', borderRadius: 'var(--radius-lg)',
      border: '1px solid var(--border-light)', overflow: 'hidden',
    }}>
      <div style={{
        padding: '16px 20px', borderBottom: '1px solid var(--border-light)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <span style={{ fontSize: 14, fontWeight: 700 }}>Session History</span>
        <button
          onClick={onExport}
          style={{
            padding: '4px 12px', borderRadius: 6, border: '1px solid var(--border)',
            background: 'var(--bg-surface)', fontSize: 12, fontWeight: 500, cursor: 'pointer',
          }}
        >
          Export CSV
        </button>
      </div>

      <table className="v3-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr style={{ borderBottom: '1px solid var(--border-light)' }}>
            <th style={thStyle}>Date</th>
            <th style={thStyle}>User</th>
            <th style={thStyle}>Device</th>
            <th style={thStyle}>Type</th>
            <th style={thStyle}>Duration</th>
          </tr>
        </thead>
        <tbody>
          {displayed.map(s => (
            <tr key={s.id} style={{ borderBottom: '1px solid var(--border-light)' }}>
              <td style={tdStyle}>{formatDate(s.started_at)}</td>
              <td style={tdStyle}>{s.user_name || '—'}</td>
              <td style={{ ...tdStyle, fontFamily: 'var(--font-mono)' }}>{s.device_label || '—'}</td>
              <td style={tdStyle}>
                <span style={{
                  padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600,
                  background: 'var(--bg-inset)', textTransform: 'uppercase',
                }}>
                  {s.session_type}
                </span>
              </td>
              <td style={tdStyle}>
                {s.duration_seconds ? `${Math.round(s.duration_seconds / 60)}m` : s.status === 'active' ? 'Active' : '—'}
              </td>
            </tr>
          ))}
          {displayed.length === 0 && (
            <tr>
              <td colSpan={5} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-tertiary)' }}>
                No sessions recorded
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div style={{
        padding: '12px 20px', borderTop: '1px solid var(--border-light)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        fontSize: 12, color: 'var(--text-tertiary)',
      }}>
        <span>
          Total: {sessions.stats.total_this_month} sessions this month
          {sessions.stats.most_accessed_device && (
            <> &middot; Most accessed: {sessions.stats.most_accessed_device} ({sessions.stats.most_accessed_count} sessions)</>
          )}
        </span>
        {sessions.total > 10 && (
          <button
            onClick={onToggleAll}
            style={{ background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: 12, fontWeight: 500 }}
          >
            {showAll ? 'Show less' : `View all ${sessions.total}`}
          </button>
        )}
      </div>
    </div>
  )
}

function formatDate(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '8px 16px', fontSize: 11,
  fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase',
  letterSpacing: '0.05em',
}

const tdStyle: React.CSSProperties = {
  padding: '10px 16px',
}
